import FadeFromTop from "../../../components/animations/FadeFromTop"
import PageContainer from "../../../components/global/PageContainer"
import Table from "../../../components/global/Table"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { colors } from "../../../services/config/colors"
import TableRow from "../../../components/global/TableRow"
import { Stack, TableCell } from "@mui/material"
import Text from "../../../components/global/Text"
import { MainContext } from "../../../controllers/main"
import { formatNumber } from "../../../services/utils/utils"
import { AuthContext } from "../../../controllers/auth"
import { useParams } from "react-router-dom"
import { ChallengesContext } from "../../../controllers/challenges"
import ChallengeGroupsLeaderboardItem from "../../../models/challengeGroupsLeaderboardItem"
import { useTranslation } from "react-i18next"

const PastChallengeGroupsLeaderboard = () => {
  const { t } = useTranslation()
  const params = useParams()
  const { team } = useContext(AuthContext)
  const { groupsList, selectedFilters } = useContext(MainContext)
  const { getPastChallengeGroupsLeaderboard, setChallengesDataLoading } =
    useContext(ChallengesContext)

  // get leaderboard items
  const [loading, setLoading] = useState<boolean>(true)
  const [leaderboard, setLeaderboard] = useState<
    ChallengeGroupsLeaderboardItem[]
  >([])
  const [leaderboardNextToken, setLeaderboardNextToken] = useState<string>()

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setChallengesDataLoading(true)

      const result = await getPastChallengeGroupsLeaderboard(
        team!.id,
        params.challengeId!
      )

      setLeaderboard(result.items)
      setLeaderboardNextToken(result.nextToken)
      setChallengesDataLoading(false)
      setLoading(false)
    }

    fetchData()
  }, [t, selectedFilters])

  // page container onScroll event to fetch new items on scroll
  const fetchingMoreItems = useRef<boolean>(false)
  const [loadingMore, setLoadingMore] = useState<boolean>(false)

  const onScroll = async () => {
    const mainContainer = document.getElementById("page-container")!

    if (
      mainContainer.scrollTop + 50 >=
        mainContainer.scrollHeight - window.innerHeight &&
      !fetchingMoreItems.current &&
      leaderboardNextToken &&
      !loading
    ) {
      fetchingMoreItems.current = true
      setLoadingMore(true)
      setChallengesDataLoading(true)

      const result = await getPastChallengeGroupsLeaderboard(
        team!.id,
        params.challengeId!,
        leaderboardNextToken
      )

      setLeaderboard((current) => [...current, ...result.items])
      setLeaderboardNextToken(result.nextToken)

      setChallengesDataLoading(true)
      setLoadingMore(false)
      fetchingMoreItems.current = false
    }
  }

  // table heading items
  const headingItems = useMemo(() => {
    return [
      loading ? (
        t("groups")
      ) : (
        <>
          {t("groups")}{" "}
          <span style={{ color: colors.textSecondary }}>
            ({leaderboard.length})
          </span>
        </>
      ),
      t("total_points"),
    ]
  }, [loading, leaderboard])

  return (
    <PageContainer
      title={t("groups_leaderboard")}
      breadcrumbs={[
        { title: t("challenges"), path: "/community-missions" },
        { title: t("groups_leaderboard") },
      ]}
      variant="compact"
      withDatePicker={false}
      onScroll={onScroll}
      csvData={[
        [t("position"), t("group"), t("total_points")],
        ...leaderboard.map((item) => [
          item.groupPosition.toString(),
          groupsList.find((group) => group.id === item.groupId)?.name ??
            t("no_group"),
          formatNumber(item.groupPoints),
        ]),
      ]}
      csvFileName="groups_leaderboard"
      backPath="/community-missions"
    >
      <FadeFromTop>
        <Table
          headingItems={headingItems}
          loading={loading}
          loadingMore={loadingMore}
        >
          {leaderboard.map((item, index) => (
            <TableRow index={index} key={item.groupId}>
              <TableCell>
                <Stack direction="row" alignItems="center" style={{ gap: 20 }}>
                  <Text fontSize={14} fontWeight={500}>
                    {item.groupPosition}°
                  </Text>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "100%",
                      backgroundImage: `url(https://cdn.aworld.io/users/default/profile.jpg)`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <Text fontSize={14} fontWeight={500}>
                    {groupsList.find((group) => group.id === item.groupId)
                      ?.name ?? t("no_group")}
                  </Text>
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Text fontSize={14} fontWeight={500}>
                    {formatNumber(item.groupPoints)}
                  </Text>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </FadeFromTop>
    </PageContainer>
  )
}

export default PastChallengeGroupsLeaderboard
